import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import useUser from '../../hooks/useUser.js';
import GlobalContext from "../../contexts/global.js";
import StateContext from "../../contexts/state.js";
import { getPageContentByUrn, getPagePreviewContentByIndex } from '../../helpers/api/getPage.js';
import RelatedContent from '../../components/RelatedContent.js';
import PageCard from '../../components/PageCard.js';
import PageBanner from '../../components/PageBanner.js';
import { changeBrowserTitle } from '../../hooks/headAmender.js';
import formatAssetUrl from '../../helpers/formatAssetUrl.js';
import locationBadgeIcon from '../../helpers/locationBadgeIcon.js';
import PageNotFound from './PageNotFound.js';
import RichText from '../../components/RichTextContent.js';

import '../../styles/story.css';

const Story = ({ prefix, preview = false }) => {
    const { globalContext } = useContext(GlobalContext);
    const { state, updateState } = useContext(StateContext);
    const { logout } = useUser();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [pageFailedLoading, setPageFailedLoading] = useState(false);
    const [content, setContent] = useState({});
    const [contentBodyParsed, setContentBodyParsed] = useState("");
    const [date, setDate] = useState(new Date());

    const apiEndPoint = process.env.REACT_APP_FETCH_URL;
    const urn = location.pathname.replace(new RegExp(`^${prefix}/?`), "");

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const response = preview ? await getPagePreviewContentByIndex({ id: urn }) : await getPageContentByUrn({ urn, logout });
                if(!isMounted) {
                    return false;
                }
                if(response) {
                    setContent(response.page);
                    // Update all api calls in the page body (asset references for instance) to go to the correct api location
                    if(apiEndPoint) {
                        setContentBodyParsed(response.page?.content.replace(/(["'])\/api\//g, `$1${apiEndPoint}/api/`));
                    }
                    const pageDateString = response.publishDate || response.page?.updated || response.page?.created;
                    if(pageDateString) {
                        setDate(new Date(pageDateString));
                    }
                    
                    /* Define current categories */
                    updateState({
                        currentCategories: response.page?.category
                    });
                }
                setLoading(false);
                setPageFailedLoading(false);
            }
            catch (error) {
                isMounted && setPageFailedLoading(true);
            }
        })();
        return () => {
            updateState({
                currentCategories: [] /* Unset categories on story page exit */
            });
            isMounted = false;
        }
    }, [location]);

    useEffect(() => {
        if(!content?.title) {
            return;
        }

        updateState({
            banner: <PageBanner crumbLabel={content.title} />
        });

        return () => {
            updateState({ banner: null });
        };
    }, [content?.title]);

    if(loading) {
        if(pageFailedLoading) {
            return <PageNotFound/>;
        }
        return <span>Loading content ...</span>;
    }

    /* Set browser title */
    content?.title && changeBrowserTitle(`${content.title}${globalContext?.siteConfig?.siteName ? ` - ${globalContext.siteConfig.siteName}` : ""}`);

    const storyImage = formatAssetUrl({
        path: content?.image,
        fallback: `${process.env.PUBLIC_URL}/assets/images/hero-placeholder.jpg`
    });

    return (
        <>
            { preview && (
                 <section className="story">
                    <p className="preview-notice">
                        <strong>You are currently in a preview mode.</strong><br/>
                        Please note page date corresponds to the page version last update and is not the same as a real publish date.<br/>
                        Related content doesn&apos;t filter for live pages only and is not showing page titles, publish dates nor cover images in preview.
                    </p>
                </section>
            ) }

            <section className="story">
                <PageCard
                    page={{ urn, index: content.index, publishDate: date, data: content }}
                    badgeKeys={[
                        { key: "los", icon: "people" },
                        { key: "location", icon: locationBadgeIcon }
                    ]}
                    showFooter={!preview}
                >
                    <h1 className="title">{content.title}</h1>

                    { content?.image && (
                        <img
                            className="hero-image"
                            src={storyImage}
                            alt={content.title || ""}
                        />
                    )}

                    <RichText
                        className="content"
                        html={contentBodyParsed || content.content}
                        runScripts={content.contentFormat !== "blocks"}
                    />
                </PageCard>

                { content.related && (
                    <div className="related-articles">
                        <RelatedContent
                            heading="Related articles"
                            content={ content.related }
                        />
                    </div>
                ) }
            </section>
        </>
    );
};

export default Story;
